import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaRupeeSign,
  FaTimes,
  FaArrowRight,
} from "react-icons/fa";
import { FaMapMarkedAlt, FaClipboardList, FaCheckCircle, FaFileAlt } from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Close } from "@mui/icons-material";

// Distict list will be derived from fetched data

export default function Report() {

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [districtData, setDistrictData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [schemes, setSchemes] = useState([]);
  const [schemeStats, setSchemeStats] = useState({
    totalSchemes: 0,
    totalDraftSchemes: 0,
    totalCompletedSchemes: 0,
    totalAllocationAmount: 0,
  });
  const [districtWiseStats, setDistrictWiseStats] = useState([]);

  const fetchSchemes = async () => {
    try {
      setLoading(true);

      const params = {
        pageNo: 1,
        pageSize: 10000,
        userWise: "true",
      };

      // Fetch all schemes for details
      const { data: schemesData } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setSchemes(schemesData?.data || []);

      // Fetch report analytics
      const { data: reportData } = await axios.get(`${server}/scheme/report`, {
        params: { pageNo: 1, pageSize: 1000 },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (reportData?.success) {
        setSchemeStats(reportData.data.totalStats);

        // Consolidate district stats (case-insensitive)
        const consolidated = reportData.data.districtWiseStats.reduce((acc, curr) => {
          const districtName = curr.district.trim().toUpperCase();
          if (!acc[districtName]) {
            acc[districtName] = { ...curr, district: curr.district.trim() };
          } else {
            acc[districtName].totalSchemes += curr.totalSchemes;
            acc[districtName].totalDraftSchemes += curr.totalDraftSchemes;
            acc[districtName].totalCompletedSchemes += curr.totalCompletedSchemes;
            acc[districtName].totalAllocationAmount += curr.totalAllocationAmount;
          }
          return acc;
        }, {});

        const sortedDistricts = Object.values(consolidated).sort((a, b) =>
          a.district.localeCompare(b.district)
        );

        setDistrictWiseStats(sortedDistricts);
      }
    } catch (error) {
      console.error("Error fetching schemes:", error);
      toast.error("Failed to fetch schemes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, []);



  const handleDistrictClick = (district) => {
    const filteredData = schemes.filter(
      (item) =>
        item.district &&
        item.district.toLowerCase().includes(district.name.toLowerCase())
    );
    setSelectedDistrict(district);
    setDistrictData(filteredData);
    setModalOpen(true);
  };

  const filteredDistricts = districtWiseStats.filter((district) =>
    district.district.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
  };

  return (
    <motion.div
      className="min-h-screen bg-[#F8FAFC] p-4 md:p-8"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header Section */}
      <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">
            Jharkhand Scheme <span className="text-blue-600">Analytics</span>
          </h1>
          <p className="text-slate-500 mt-2 text-lg">
            Real-time monitoring and reporting of state-wide developmental schemes.
          </p>
        </div>

        {/* Search Bar - Modern Look */}
        <div className="relative w-full md:w-96 group">
          <div className="absolute inset-0 bg-blue-100 blur-lg opacity-0 group-focus-within:opacity-50 transition-opacity duration-300 rounded-2xl"></div>
          <div className="relative flex items-center">
            <FaSearch className="absolute left-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search districts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-12 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-700 placeholder:text-slate-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-4 text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <StatCard
          icon={<FaClipboardList />}
          label="Total Schemes"
          value={schemeStats.totalSchemes}
          gradient="from-indigo-500 to-blue-600"
          delay={0.1}
        />
        <StatCard
          icon={<FaFileAlt />}
          label="Draft Schemes"
          value={schemeStats.totalDraftSchemes}
          gradient="from-amber-400 to-orange-500"
          delay={0.2}
        />
        <StatCard
          icon={<FaCheckCircle />}
          label="Completed"
          value={schemeStats.totalCompletedSchemes}
          gradient="from-emerald-400 to-teal-600"
          delay={0.3}
        />
        <StatCard
          icon={<FaRupeeSign />}
          label="Total Allocation"
          value={`₹${(schemeStats.totalAllocationAmount / 10000000).toFixed(2)} Cr`}
          gradient="from-rose-500 to-pink-600"
          delay={0.4}
        />
      </div>

      {/* District Cards Grid */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
          <MdLocationOn className="text-blue-600" />
          District-wise Performance
        </h2>
        <span className="text-sm font-medium text-slate-500 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm">
          {filteredDistricts.length} Districts Showing
        </span>
      </div>

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        variants={containerVariants}
      >
        <AnimatePresence>
          {filteredDistricts.map((district) => {
            return (
              <motion.div
                key={district.district}
                variants={itemVariants}
                layout
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                onClick={() => handleDistrictClick({ name: district.district })}
                className="group relative bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow-lg hover:border-blue-200 cursor-pointer transition-all duration-300"
              >
                {/* Card Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>

                <div className="relative">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                      <FaMapMarkedAlt className="text-lg" />
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Jharkhand</span>
                      <h3 className="text-lg font-bold text-slate-800 mt-0.5">
                        {district.district}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-xl group-hover:bg-white transition-colors">
                      <span className="text-xs text-slate-500 font-medium">Allocated</span>
                      <span className="text-xs font-bold text-slate-900">
                        ₹{(district.totalAllocationAmount / 100000).toFixed(2)} L
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-xl group-hover:bg-white transition-colors">
                      <span className="text-xs text-slate-500 font-medium">Total Schemes</span>
                      <span className="text-xs font-bold text-slate-900">
                        {district.totalSchemes}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-blue-600 font-bold text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>View Analytics</span>
                    <FaArrowRight />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {/* Empty State */}
      {filteredDistricts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-200">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6">
            <FaSearch className="text-4xl text-slate-300" />
          </div>
          <h3 className="text-2xl font-bold text-slate-800">No results found</h3>
          <p className="text-slate-500 mt-2">Try adjusting your search to find the district.</p>
          <button
            onClick={() => setSearchQuery("")}
            className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
          >
            Clear Search
          </button>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-10 rounded-3xl shadow-2xl flex flex-col items-center">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-blue-600/10 rounded-full"></div>
              </div>
            </div>
            <p className="mt-6 text-slate-800 font-bold text-lg italic">Gathering Intelligence...</p>
          </div>
        </div>
      )}

      {/* District Detail Modal */}
      <DistrictDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        districtData={districtData}
        selectedDistrict={selectedDistrict}
      />
    </motion.div>
  );
}

// Sub-component for Stats
function StatCard({ icon, label, value, gradient, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`relative overflow-hidden bg-gradient-to-br ${gradient} p-5 rounded-2xl text-white shadow-lg shadow-blue-100 group`}
    >
      {/* Decorative circles */}
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
      <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-black/10 rounded-full blur-2xl"></div>

      <div className="relative flex flex-col h-full">
        <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center text-xl mb-4 shadow-inner">
          {icon}
        </div>
        <span className="text-white/80 font-medium tracking-wide uppercase text-[10px]">
          {label}
        </span>
        <div className="text-2xl font-black mt-0.5 tracking-tight">
          {value}
        </div>
      </div>
    </motion.div>
  );
}

// District Detail Modal Component
function DistrictDetailModal({
  open,
  onClose,
  districtData,
  selectedDistrict,
}) {
  const navigate = useNavigate();
  if (!selectedDistrict) return null;

  const totalAllocated = districtData.reduce((sum, item) => sum + (item.allocatedAmount || 0), 0);
  const totalSchemes = districtData.length;
  const completedSchemes = districtData.filter(item => item.schemeStatus === "Complete" || item.progressPercentage === 100).length;
  const progressPercent = totalSchemes > 0 ? (completedSchemes / totalSchemes) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div className="bg-blue-600 p-6 text-white flex justify-between items-center">
        <div>
          <h2 className="text-2xl text-white font-bold">{selectedDistrict.name} District</h2>
          <p className="text-blue-100 text-sm mt-1">Jharkhand State • Statistics Overview</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("../reportSchemes", { state: { district: selectedDistrict.name } })}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg font-bold hover:bg-blue-50 transition-colors shadow-sm"
          >
            View Detailed List
          </button>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </div>
      </div>

      <DialogContent className="p-8">
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Total Allocated</p>
            <h3 className="text-lg font-bold text-gray-900 mt-0.5">₹{(totalAllocated / 100000).toFixed(2)} L</h3>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Total Schemes</p>
            <h3 className="text-lg font-bold text-gray-900 mt-0.5">{totalSchemes}</h3>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Success Rate</p>
            <h3 className="text-lg font-bold text-emerald-600 mt-0.5">{progressPercent.toFixed(1)}%</h3>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Completed</p>
            <h3 className="text-lg font-bold text-blue-600 mt-0.5">{completedSchemes}</h3>
          </div>
        </div>

        {/* Detailed Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column: Progress */}
          <div className="space-y-6">
            <div>
              <h4 className="text-gray-800 font-bold mb-3">Implementation Progress</h4>
              <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-gray-600 font-medium">Status: {progressPercent === 100 ? "Completed" : "In Progress"}</span>
                <span className="text-blue-600 font-bold">{progressPercent.toFixed(1)}%</span>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
              <h4 className="text-blue-800 font-bold text-sm mb-2">Operational Insight</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-blue-600">Active DDO Units:</span>
                  <span className="font-bold text-blue-900">{[...new Set(districtData.map(d => d.ddoCode))].filter(Boolean).length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-blue-600">Primary Sector:</span>
                  <span className="font-bold text-blue-900">{districtData[0]?.sector || "Infrastructure"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Financials */}
          <div className="space-y-4">
            <h4 className="text-gray-800 font-bold">Financial Summary</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm">Avg. Cost Per Scheme</span>
                <span className="font-bold text-gray-900">₹{(totalAllocated / (totalSchemes || 1) / 1000).toFixed(2)} K</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm">Utilization Status</span>
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">Healthy</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm">Last Audit Date</span>
                <span className="font-medium text-gray-900">28 Apr, 2026</span>
              </div>
            </div>


          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
